import { useEffect, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, Image } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, Home, Calendar, Phone, Clock, ExternalLink, MessageSquare, X, Upload, Image as ImageIcon, Trash2, AlertTriangle, Link2, Copy, Eye, Mail, CheckCircle2, CalendarCheck, ChevronRight, ChevronDown } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';
import { Profile, Home as HomeType, ServiceRequest } from './types';
import AdminLayout from './AdminLayout';
import { startImpersonation } from '../../lib/impersonation';
import ReferringAgentField from './ReferringAgentField';

// Helper component to render div on web, View on native
const LayoutContainer = ({ className, style, children, ...props }: any) => {
  if (Platform.OS === 'web') {
    return <div className={className} style={style} {...props}>{children}</div>;
  }
  return <View style={style} {...props}>{children}</View>;
};

// Custom Dropdown Picker Component
const DropdownPicker = ({ 
  visible, 
  onClose, 
  options, 
  selectedValue, 
  onSelect 
}: { 
  visible: boolean; 
  onClose: () => void; 
  options: Array<{ value: string; label: string }>; 
  selectedValue: string; 
  onSelect: (value: string) => void;
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={pickerStyles.pickerModalOverlay}>
        <Pressable style={pickerStyles.pickerModalBackdrop} onPress={onClose} />
        <View style={pickerStyles.pickerModalContent}>
          <View style={pickerStyles.pickerHeader}>
            <Text style={pickerStyles.pickerHeaderText}>Select Option</Text>
            <Pressable onPress={onClose}>
              <X size={20} color="#6b7280" />
            </Pressable>
          </View>
          <WebScrollView style={pickerStyles.pickerOptions}>
            {options.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  onClose();
                }}
                style={[
                  pickerStyles.pickerOption,
                  selectedValue === option.value && pickerStyles.pickerOptionSelected
                ]}
              >
                <Text style={[
                  pickerStyles.pickerOptionText,
                  selectedValue === option.value && pickerStyles.pickerOptionTextSelected
                ]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

interface HomeownerRequestCapacity {
  profile_id: string;
  active_count: number;
  backburner_count: number;
  effective_limit: number;
  over_limit_count: number;
}

const getHomePlanRollup = (homes: HomeType[]) => {
  const homeCount = homes.length;
  const managedHomeCount = homes.filter((home) => home.plan === 'managed').length;

  if (managedHomeCount > 0) {
    return {
      isPremium: true,
      label: homeCount > 1 ? `Premium (${managedHomeCount} of ${homeCount} homes)` : 'Premium',
    };
  }

  return {
    isPremium: false,
    label: homeCount > 1 ? `Basic (0 of ${homeCount} homes)` : 'Basic',
  };
};

const ProfileDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [homes, setHomes] = useState<HomeType[]>([]);
  const [editingHomeId, setEditingHomeId] = useState<string | null>(null);
  const [editedHome, setEditedHome] = useState<Partial<HomeType>>({});
  const [savingHome, setSavingHome] = useState(false);
  const [homeError, setHomeError] = useState<string | null>(null);
  const [showAddHomeModal, setShowAddHomeModal] = useState(false);
  const [addingHome, setAddingHome] = useState(false);
  const [showAddHomeManagerPicker, setShowAddHomeManagerPicker] = useState(false);
  const [newHomeData, setNewHomeData] = useState({
    address: '',
    city: '',
    state: '',
    zip_code: '',
    square_footage: '',
    year_built: '',
    home_manager: 'Brandon Takens',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editedProfile, setEditedProfile] = useState<Partial<Profile>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState<ServiceRequest | null>(null);
  const [showServiceRequestModal, setShowServiceRequestModal] = useState(false);
  const [expandedServiceRequestIds, setExpandedServiceRequestIds] = useState<Set<string>>(new Set());
  const [visits, setVisits] = useState<any[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [showLifecyclePicker, setShowLifecyclePicker] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [claimLink, setClaimLink] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [creatingStripeCustomer, setCreatingStripeCustomer] = useState(false);
  const [stripeCustomerError, setStripeCustomerError] = useState<string | null>(null);
  const [showStripeCustomerModal, setShowStripeCustomerModal] = useState(false);
  const [existingCustomerId, setExistingCustomerId] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailUpdateError, setEmailUpdateError] = useState<string | null>(null);
  const [emailUpdateSuccess, setEmailUpdateSuccess] = useState<string | null>(null);
  const [updatingOnboarding, setUpdatingOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [requestCapacity, setRequestCapacity] = useState<HomeownerRequestCapacity | null>(null);
  const [loadingRequestCapacity, setLoadingRequestCapacity] = useState(false);
  const [newActiveLimit, setNewActiveLimit] = useState('');
  const [capacityReason, setCapacityReason] = useState('');
  const [savingRequestLimit, setSavingRequestLimit] = useState(false);
  const [requestCapacityError, setRequestCapacityError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      navigate('/admin');
      return;
    }
    fetchProfileData();
  }, [userId]);

  useEffect(() => {
    if (homes.length > 0) {
      fetchServiceRequests();
      fetchVisits();
    }
  }, [homes]);

  useEffect(() => {
    if (!userId) return;
    fetchRequestCapacity(userId);
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*, sent_welcome_card_at, had_walkthrough')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Fetch associated homes
      const { data: homesData, error: homesError } = await supabase
        .from('homes')
        .select('*')
        .eq('owner_id', userId);

      if (homesError) throw homesError;

      setProfile(profileData);
      setHomes(homesData || []);

      // Fetch user email from auth.users via database function
      try {
        const { data: email, error: emailError } = await supabase.rpc('get_user_email', {
          target_user_id: userId
        });

        if (emailError) {
          console.error('Error fetching email:', emailError);
          setUserEmail('N/A');
          setEmailInput('');
        } else {
          setUserEmail(email || 'N/A');
          setEmailInput(email || '');
        }
      } catch (emailError) {
        // Silently fail - email is optional
        console.error('Error fetching email:', emailError);
        setUserEmail('N/A');
        setEmailInput('');
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceRequests = async () => {
    try {
      setLoadingRequests(true);
      
      // Get all home IDs for this user
      const homeIds = homes.map(h => h.id);
      
      if (homeIds.length === 0) {
        setServiceRequests([]);
        return;
      }
      
      // Fetch service requests for all user's homes
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          priority,
          status,
          is_backburner,
          created_at,
          home_id,
          visit_id,
          visit_scheduled_at,
          visit_completed_at,
          owner_id,
          home:homes (
            address,
            city,
            state
          )
        `)
        .in('home_id', homeIds)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform the data to match our interface (home is returned as array from Supabase join)
      const transformedData = (data || []).map((request: any) => ({
        ...request,
        home: Array.isArray(request.home) ? request.home[0] : request.home
      }));
      
      setServiceRequests(transformedData);
    } catch (error: any) {
      console.error('Error fetching service requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchRequestCapacity = async (targetProfileId: string) => {
    try {
      setLoadingRequestCapacity(true);
      setRequestCapacityError(null);
      const { data, error } = await supabase.rpc('admin_homeowner_request_capacity', {
        target_profile_id: targetProfileId,
      });

      if (error) throw error;

      const capacity = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setRequestCapacity(capacity);
      if (capacity?.effective_limit !== undefined && capacity?.effective_limit !== null) {
        setNewActiveLimit(String(capacity.effective_limit));
      }
    } catch (capacityError: any) {
      console.error('Error fetching homeowner request capacity:', capacityError);
      setRequestCapacityError(capacityError?.message || 'Failed to load request capacity');
      setRequestCapacity(null);
    } finally {
      setLoadingRequestCapacity(false);
    }
  };

  const handleSaveRequestLimit = async () => {
    if (!userId) return;

    const parsedLimit = parseInt(newActiveLimit, 10);
    if (Number.isNaN(parsedLimit) || parsedLimit < 0) {
      setRequestCapacityError('Active request limit must be a non-negative number.');
      return;
    }

    try {
      setSavingRequestLimit(true);
      setRequestCapacityError(null);

      const { error } = await supabase
        .from('homeowner_request_limits')
        .insert({
          profile_id: userId,
          active_limit: parsedLimit,
          reason: capacityReason.trim() || null,
        });

      if (error) throw error;

      setCapacityReason('');
      await fetchRequestCapacity(userId);
    } catch (saveCapacityError: any) {
      console.error('Error saving homeowner request limit:', saveCapacityError);
      setRequestCapacityError(saveCapacityError?.message || 'Failed to save request limit.');
    } finally {
      setSavingRequestLimit(false);
    }
  };

  const fetchVisits = async () => {
    try {
      setLoadingVisits(true);
      
      // Get all home IDs for this user
      const homeIds = homes.map(h => h.id);
      
      if (homeIds.length === 0) {
        setVisits([]);
        return;
      }
      
      // Fetch visits for all user's homes
      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          visit_type,
          status,
          scheduled_date,
          actual_start_time,
          actual_end_time,
          assigned_to,
          visit_notes,
          created_at,
          home_id,
          home:homes (
            address,
            city,
            state
          )
        `)
        .in('home_id', homeIds)
        .order('scheduled_date', { ascending: false });
      
      if (error) throw error;
      
      // Transform the data (home is returned as array from Supabase join)
      const transformedData = (data || []).map((visit: any) => ({
        ...visit,
        home: Array.isArray(visit.home) ? visit.home[0] : visit.home
      }));
      
      setVisits(transformedData);
    } catch (error: any) {
      console.error('Error fetching visits:', error);
    } finally {
      setLoadingVisits(false);
    }
  };

  const handleEditHome = (home: HomeType) => {
    setEditingHomeId(home.id);
    setEditedHome(home);
    setHomeError(null);
  };

  const handleCancelHomeEdit = () => {
    setEditingHomeId(null);
    setEditedHome({});
    setHomeError(null);
  };

  const handleHomeChange = (field: keyof HomeType, value: any) => {
    setEditedHome(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('homes')
        .upload(filePath, file);

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          throw new Error('Storage not configured. Please contact support.');
        }
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('homes')
        .getPublicUrl(filePath);

      handleHomeChange('cover_photo_url', publicUrl);
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveHome = async () => {
    try {
      setSavingHome(true);
      setHomeError(null);

      // Get the current user's session to ensure they're a system manager
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update homes');
      }

      // Update the home
      const { error: updateError } = await supabase
        .from('homes')
        .update({
          address: editedHome.address,
          city: editedHome.city,
          state: editedHome.state,
          zip_code: editedHome.zip_code,
          square_footage: editedHome.square_footage || null,
          year_built: editedHome.year_built || null,
          cover_photo_url: editedHome.cover_photo_url || null,
          lot_size: editedHome.lot_size || null,
          bedrooms: editedHome.bedrooms || null,
          bathrooms: editedHome.bathrooms || null,
          home_notes: editedHome.home_notes || null
        })
        .eq('id', editingHomeId);

      if (updateError) throw updateError;

      // Refresh the homes data
      const { data: homesData, error: homesError } = await supabase
        .from('homes')
        .select('*')
        .eq('owner_id', userId);

      if (homesError) throw homesError;

      setHomes(homesData || []);
      setEditingHomeId(null);
      setEditedHome({});
    } catch (error: any) {
      console.error('Error saving home:', error);
      setHomeError(error.message);
    } finally {
      setSavingHome(false);
    }
  };

  const handleCreateHome = async () => {
    if (!userId) return;
    if (!newHomeData.address.trim() || !newHomeData.city.trim() || !newHomeData.state.trim() || !newHomeData.zip_code.trim()) {
      setHomeError('Address, city, state, and ZIP code are required.');
      return;
    }

    try {
      setAddingHome(true);
      setHomeError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can create homes');
      }

      const { data: createdHome, error: createHomeError } = await supabase
        .from('homes')
        .insert({
          owner_id: userId,
          address: newHomeData.address.trim(),
          city: newHomeData.city.trim(),
          state: newHomeData.state.trim(),
          zip_code: newHomeData.zip_code.trim(),
          square_footage: newHomeData.square_footage ? parseInt(newHomeData.square_footage, 10) : null,
          year_built: newHomeData.year_built ? parseInt(newHomeData.year_built, 10) : null,
          home_manager: newHomeData.home_manager,
        })
        .select('id')
        .single();

      if (createHomeError) throw createHomeError;

      let homeManagerId: string | null = null;
      if (newHomeData.home_manager && newHomeData.home_manager !== 'Unassigned') {
        const { data: managerProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('is_system_manager', true)
          .eq('full_name', newHomeData.home_manager)
          .maybeSingle();
        homeManagerId = managerProfile?.id || null;
      }

      if (createdHome?.id) {
        await supabase
          .from('conversations')
          .insert({
            homeowner_id: userId,
            type: 'home_manager',
            home_id: createdHome.id,
            home_manager_id: homeManagerId,
          });
      }

      setShowAddHomeModal(false);
      setNewHomeData({
        address: '',
        city: '',
        state: '',
        zip_code: '',
        square_footage: '',
        year_built: '',
        home_manager: 'Brandon Takens',
      });
      await fetchProfileData();
    } catch (createHomeError: any) {
      console.error('Error creating home:', createHomeError);
      setHomeError(createHomeError.message || 'Failed to create home');
    } finally {
      setAddingHome(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      // Get the current user's session to ensure they're a system manager
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update profiles');
      }

      const profileUpdates = { ...editedProfile };
      delete profileUpdates.plan;
      delete profileUpdates.home_count;
      delete profileUpdates.managed_home_count;

      // Update the profile. Plan is now home-level and managed through home billing/admin actions.
      const { error: updateError } = await supabase
        .from('profiles')
        .update(profileUpdates)
        .eq('id', userId);

      if (updateError) throw updateError;

      // Refresh the profile data
      await fetchProfileData();
      setIsEditing(false);
      setEditedProfile({});
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedProfile(profile || {});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile({});
    setSaveError(null);
  };

  const handleChange = (field: keyof Profile, value: any) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteUser = async () => {
    if (!userId) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      // Get the current user's session to ensure they're a system manager
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can delete users');
      }

      // Prevent self-deletion
      if (user.id === userId) {
        throw new Error('Cannot delete your own account');
      }

      // Call the delete_user function
      const { error: deleteError } = await supabase.rpc('delete_user', {
        target_user_id: userId
      });

      if (deleteError) throw deleteError;

      // Navigate back to admin page on success
      navigate('/admin');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setDeleteError(error.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateClaimLink = async () => {
    try {
      setGeneratingLink(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use direct fetch to ensure authorization header is included
      const functionUrl = `${SUPABASE_URL}/functions/v1/generate-claim-link`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ 
          userId,
          redirectTo: Platform.OS === 'web' && typeof window !== 'undefined' 
            ? `${window.location.origin}/claim`
            : 'https://app.honeydone.com/claim'
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Function response error:', errorText);
        throw new Error(`Failed to generate claim link: ${response.status} ${errorText}`);
      }

      const functionData = await response.json();

      if (!functionData || functionData.error) {
        throw new Error(functionData?.error || 'Failed to generate claim link');
      }
      
      setClaimLink(functionData.url);
      setShowLinkModal(true);
    } catch (error: any) {
      console.error('Error generating link:', error);
      setError(error.message || 'Failed to generate claim link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (claimLink) {
      if (Platform.OS === 'web') {
        navigator.clipboard.writeText(claimLink);
        // You might want to show a toast here, but for now visual feedback on button is enough
      }
    }
  };

  const handleImpersonateUser = async () => {
    if (!profile) return;
    
    if (!window.confirm(`Are you sure you want to impersonate ${profile.full_name}?\n\nThis will switch your session to view the app as this user.`)) {
      return;
    }

    try {
      setImpersonating(true);
      setError(null);
      await startImpersonation(userId!);
      // startImpersonation will redirect, so this line won't execute
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      setError(`Failed to impersonate user: ${error.message}`);
      setImpersonating(false);
    }
  };

  const handleStartEmailEdit = () => {
    setEmailInput(userEmail && userEmail !== 'N/A' ? userEmail : '');
    setEmailUpdateError(null);
    setEmailUpdateSuccess(null);
    setIsEditingEmail(true);
  };

  const handleCancelEmailEdit = () => {
    setIsEditingEmail(false);
    setEmailInput(userEmail && userEmail !== 'N/A' ? userEmail : '');
    setEmailUpdateError(null);
    setEmailUpdateSuccess(null);
  };

  const handleUpdateEmail = async () => {
    if (!userId) return;

    const nextEmail = emailInput.trim().toLowerCase();
    if (!nextEmail) {
      setEmailUpdateError('Email is required');
      setEmailUpdateSuccess(null);
      return;
    }

    if (nextEmail === (userEmail || '').toLowerCase()) {
      setEmailUpdateError('That email is already set for this user');
      setEmailUpdateSuccess(null);
      return;
    }

    try {
      setUpdatingEmail(true);
      setEmailUpdateError(null);
      setEmailUpdateSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const functionUrl = `${SUPABASE_URL}/functions/v1/update-user-email`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          userId,
          email: nextEmail,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to update email');
      }

      const updatedEmail = data.email || nextEmail;
      setUserEmail(updatedEmail);
      setEmailInput(updatedEmail);
      setIsEditingEmail(false);
      setEmailUpdateSuccess('Email updated successfully.');
    } catch (emailError: any) {
      console.error('Error updating email:', emailError);
      setEmailUpdateError(emailError.message || 'Failed to update email');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleUpdateWelcomeCard = async () => {
    if (!userId || !profile) return;

    try {
      setUpdatingOnboarding(true);
      setOnboardingError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update onboarding status');
      }

      const newDate = profile.sent_welcome_card_at ? null : new Date().toISOString();

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ sent_welcome_card_at: newDate })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Refresh the profile data
      await fetchProfileData();
    } catch (error: any) {
      console.error('Error updating welcome card:', error);
      setOnboardingError(error.message);
    } finally {
      setUpdatingOnboarding(false);
    }
  };

  const handleUpdateWalkthrough = async () => {
    if (!userId || !profile) return;

    try {
      setUpdatingOnboarding(true);
      setOnboardingError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify the current user is a system manager
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', user.id)
        .single();

      if (!currentProfile?.is_system_manager) {
        throw new Error('Only system managers can update onboarding status');
      }

      const newValue = !profile.had_walkthrough;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ had_walkthrough: newValue })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Refresh the profile data
      await fetchProfileData();
    } catch (error: any) {
      console.error('Error updating walkthrough:', error);
      setOnboardingError(error.message);
    } finally {
      setUpdatingOnboarding(false);
    }
  };

  const handleCreateStripeCustomer = async (useExistingId: boolean = false) => {
    if (!userId || !profile) return;

    try {
      setCreatingStripeCustomer(true);
      setStripeCustomerError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Call edge function to create or link Stripe customer
      // The edge function will fetch the homeowner's email from auth.users
      const functionUrl = `${SUPABASE_URL}/functions/v1/create-stripe-customer`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          userId,
          fullName: profile.full_name,
          phoneNumber: profile.phone_number,
          existingCustomerId: useExistingId ? existingCustomerId : undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to create Stripe customer');
      }

      // Refresh profile data to get the new stripe_customer_id
      await fetchProfileData();
      setShowStripeCustomerModal(false);
      setExistingCustomerId('');
      
    } catch (error: any) {
      console.error('Error creating Stripe customer:', error);
      setStripeCustomerError(error.message || 'Failed to create Stripe customer');
    } finally {
      setCreatingStripeCustomer(false);
    }
  };

  const getPriorityColor = (priority: ServiceRequest['priority']) => {
    switch (priority) {
      case 'urgent': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'high': return { color: '#ea580c', backgroundColor: '#fff7ed' };
      case 'normal': return { color: '#2563eb', backgroundColor: '#eff6ff' };
      case 'low': return { color: '#16a34a', backgroundColor: '#f0fdf4' };
      default: return { color: '#4b5563', backgroundColor: '#f9fafb' };
    }
  };

  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return { color: '#16a34a', backgroundColor: '#f0fdf4' };
      case 'in_progress': return { color: '#2563eb', backgroundColor: '#eff6ff' };
      case 'cancelled': return { color: '#4b5563', backgroundColor: '#f9fafb' };
      default: return { color: '#ea580c', backgroundColor: '#fff7ed' };
    }
  };

  const handleServiceRequestClick = (request: ServiceRequest) => {
    setSelectedServiceRequest(request);
    setShowServiceRequestModal(true);
  };

  const toggleServiceRequestExpanded = (id: string) => {
    setExpandedServiceRequestIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <AdminLayout activeTab={null}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#000000" />
        </View>
      </AdminLayout>
    );
  }

  if (error || !profile) {
    return (
      <AdminLayout activeTab={null}>
        <View style={styles.container}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error || 'Profile not found'}</Text>
          </View>
        </View>
      </AdminLayout>
    );
  }

  const homePlanRollup = getHomePlanRollup(homes);

  return (
    <AdminLayout activeTab={null}>
    <WebScrollView style={styles.container}>
      {Platform.OS === 'web' && (
        <style>{`
          .responsive-grid {
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 16px;
          }
          @media (min-width: 768px) {
            .responsive-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 24px;
            }
          }
          .responsive-content {
            padding: 16px;
            max-width: 100%;
            margin: 0 auto;
            width: 100%;
          }
          @media (min-width: 768px) {
            .responsive-content {
              padding: 24px;
            }
          }
          @media (min-width: 1024px) {
            .responsive-content {
              max-width: 1400px;
            }
          }
          .main-layout {
            display: flex !important;
            flex-direction: column !important;
            gap: 24px !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          @media screen and (min-width: 1024px) {
            .main-layout {
              flex-direction: row !important;
              align-items: flex-start !important;
              justify-content: space-between !important;
            }
          }
          .main-content {
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 24px !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }
          @media screen and (min-width: 1024px) {
            .main-content {
              flex: 0 0 calc(66.666% - 12px) !important;
              max-width: calc(66.666% - 12px) !important;
              width: calc(66.666% - 12px) !important;
            }
          }
          .timeline-sidebar {
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }
          @media screen and (min-width: 1024px) {
            .timeline-sidebar {
              flex: 0 0 calc(33.333% - 12px) !important;
              max-width: calc(33.333% - 12px) !important;
              width: calc(33.333% - 12px) !important;
              position: sticky !important;
              top: 24px !important;
              align-self: flex-start !important;
              height: fit-content !important;
            }
          }
          .responsive-card {
            padding: 16px !important;
          }
          @media (min-width: 768px) {
            .responsive-card {
              padding: 24px !important;
            }
          }
          .card-full-width {
            grid-column: 1 / -1;
          }
        `}</style>
      )}
      <LayoutContainer className={Platform.OS === 'web' ? 'responsive-content' : undefined} style={Platform.OS === 'web' ? { padding: 24 } : styles.contentContainer}>
      <View style={styles.headerSection}>
        <Pressable
          onPress={() => navigate('/admin')}
          style={styles.backButton}
        >
          <ArrowLeft size={20} color="#6b7280" />
          <Text style={styles.backButtonText}>Back to Admin</Text>
        </Pressable>
        <Text style={styles.pageTitle}>User Profile</Text>
        <View style={styles.headerActions}>
          <Pressable
            onPress={handleGenerateClaimLink}
            disabled={generatingLink}
            style={styles.actionButton}
          >
            {generatingLink ? (
              <ActivityIndicator size="small" color="#4b5563" />
            ) : (
              <Link2 size={20} color="#4b5563" />
            )}
            <Text style={styles.actionButtonText}>Generate Link</Text>
          </Pressable>
          {!profile?.is_system_manager && (
            <Pressable
              onPress={handleImpersonateUser}
              disabled={impersonating}
              style={styles.actionButton}
            >
              {impersonating ? (
                <ActivityIndicator size="small" color="#4b5563" />
              ) : (
                <Eye size={20} color="#4b5563" />
              )}
              <Text style={styles.actionButtonText}>View As User</Text>
            </Pressable>
          )}
          <Pressable
            onPress={() => setShowDeleteConfirmModal(true)}
            style={styles.deleteButton}
          >
            <Trash2 size={20} color="#dc2626" />
            <Text style={styles.deleteButtonText}>Delete User</Text>
          </Pressable>
        </View>
      </View>

      <LayoutContainer className={Platform.OS === 'web' ? 'main-layout' : undefined} style={Platform.OS === 'web' ? undefined : styles.mainLayout}>
        <LayoutContainer className={Platform.OS === 'web' ? 'main-content' : undefined} style={Platform.OS === 'web' ? undefined : styles.mainContent}>
          <View style={styles.grid} {...(Platform.OS === 'web' && { className: 'responsive-grid' })}>
            {/* Basic Information */}
        <View style={styles.card} {...(Platform.OS === 'web' && { className: 'responsive-card' })}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <User size={20} color="#6b7280" />
              <Text style={styles.cardTitle}>Basic Information</Text>
            </View>
            <View style={styles.buttonRow}>
              {!isEditing ? (
                <Pressable onPress={handleEdit} style={styles.editButton}>
                  <Text style={styles.editButtonText}>Edit</Text>
                </Pressable>
              ) : (
                <>
                  <Pressable
                    onPress={handleCancel}
                    disabled={saving}
                    style={styles.cancelButton}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleSave}
                    disabled={saving}
                    style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                  >
                    <Text style={styles.saveButtonText}>
                    {saving ? 'Saving...' : 'Save'}
                    </Text>
                  </Pressable>
                </>
              )}
            </View>
          </View>
          {saveError && (
            <View style={styles.alertError}>
              <Text style={styles.alertErrorText}>{saveError}</Text>
            </View>
          )}
          <View style={styles.fieldsList}>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Full Name</Text>
              {isEditing ? (
                <TextInput
                  value={editedProfile.full_name || ''}
                  onChangeText={(value) => handleChange('full_name', value)}
                  placeholder="Enter full name"
                  style={styles.input}
                />
              ) : (
                <Text style={styles.fieldValue}>{profile.full_name || 'N/A'}</Text>
              )}
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Phone Number</Text>
              {isEditing ? (
                <TextInput
                  value={editedProfile.phone_number || ''}
                  onChangeText={(value) => handleChange('phone_number', value)}
                  placeholder="Enter phone number"
                  keyboardType="phone-pad"
                  style={styles.input}
                />
              ) : (
                <View style={styles.fieldValueRow}>
                  <Phone size={16} color="#9ca3af" />
                  <Text style={styles.fieldValue}>{profile.phone_number || 'N/A'}</Text>
                </View>
              )}
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Email Address</Text>
              {isEditingEmail ? (
                <View style={styles.emailEditor}>
                  <TextInput
                    value={emailInput}
                    onChangeText={setEmailInput}
                    placeholder="Enter email address"
                    autoCapitalize="none"
                    keyboardType="email-address"
                    style={styles.input}
                  />
                  <View style={styles.buttonRow}>
                    <Pressable
                      onPress={handleCancelEmailEdit}
                      disabled={updatingEmail}
                      style={styles.cancelButton}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleUpdateEmail}
                      disabled={updatingEmail}
                      style={[styles.saveButton, updatingEmail && styles.saveButtonDisabled]}
                    >
                      <Text style={styles.saveButtonText}>
                        {updatingEmail ? 'Updating...' : 'Update Email'}
                      </Text>
                    </Pressable>
                  </View>
                </View>
              ) : (
                <View style={styles.fieldValueRow}>
                  <Mail size={16} color="#9ca3af" />
                  <Text style={styles.fieldValue}>{userEmail ?? 'Loading...'}</Text>
                  <Pressable onPress={handleStartEmailEdit} style={styles.inlineActionButton}>
                    <Text style={styles.inlineActionButtonText}>Change</Text>
                  </Pressable>
                </View>
              )}
              {emailUpdateError ? (
                <Text style={styles.inlineErrorText}>{emailUpdateError}</Text>
              ) : null}
              {emailUpdateSuccess ? (
                <Text style={styles.inlineSuccessText}>{emailUpdateSuccess}</Text>
              ) : null}
              <Text style={styles.inlineNoteText}>
                Depending on Supabase Auth email confirmation settings, the user may need to confirm the new email from their inbox.
              </Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Plan</Text>
              <View style={[
                styles.badge,
                homePlanRollup.isPremium ? styles.badgePremium : styles.badgeBasic
              ]}>
                <Text style={[
                  styles.badgeText,
                  homePlanRollup.isPremium ? styles.badgeTextPremium : styles.badgeTextBasic
                ]}>
                  {homePlanRollup.label}
                </Text>
              </View>
              {isEditing ? (
                <Text style={styles.inlineNoteText}>
                  Plans are now managed per home from each home's billing/admin controls.
                </Text>
              ) : null}
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Lifecycle Status</Text>
              {isEditing ? (
                <Pressable
                  onPress={() => setShowLifecyclePicker(true)}
                  style={styles.pickerButton}
                >
                  <Text style={[styles.pickerButtonText, styles.capitalize]}>
                    {editedProfile.lifecycle_status || ''}
                  </Text>
                </Pressable>
              ) : (
                <Text style={[styles.fieldValue, styles.capitalize]}>
                  {profile.lifecycle_status}
                </Text>
              )}
            </View>
            <ReferringAgentField
              profileId={profile.id}
              currentAgentId={(isEditing ? editedProfile.referring_agent_id : profile.referring_agent_id) ?? null}
              currentAgentLinkedAt={profile.referring_agent_linked_at ?? null}
              isEditing={isEditing}
              onChanged={(newAgentId) => {
                setProfile((prev) => (prev ? { ...prev, referring_agent_id: newAgentId } : prev));
                setEditedProfile((prev) => ({ ...prev, referring_agent_id: newAgentId }));
              }}
            />
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Stripe Customer</Text>
              <View style={styles.fieldValueRow}>
                {profile.stripe_customer_id ? (
                  <View style={[styles.badge, styles.badgeStripeActive]}>
                    <Text style={[styles.badgeText, styles.badgeTextStripeActive]}>
                      Connected
                    </Text>
                  </View>
                ) : (
                  <>
                    <View style={[styles.badge, styles.badgeStripeInactive]}>
                      <Text style={[styles.badgeText, styles.badgeTextStripeInactive]}>
                        Not Connected
                      </Text>
                    </View>
                    {!isEditing && (
                      <Pressable
                        onPress={() => setShowStripeCustomerModal(true)}
                        disabled={creatingStripeCustomer}
                        style={styles.stripeButton}
                      >
                        <Text style={styles.stripeButtonText}>Connect Customer</Text>
                      </Pressable>
                    )}
                  </>
                )}
              </View>
              {stripeCustomerError && (
                <Text style={styles.errorText}>{stripeCustomerError}</Text>
              )}
            </View>
            {isEditing && (
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>Notes</Text>
                <TextInput
                  value={editedProfile.notes || ''}
                  onChangeText={(value) => handleChange('notes', value)}
                  placeholder="Add notes about this user..."
                  multiline
                  numberOfLines={3}
                  style={[styles.input, styles.textArea]}
                />
              </View>
            )}
            {!isEditing && profile.notes && (
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>Notes</Text>
                <View style={styles.notesBox}>
                  <Text style={styles.notesText}>{profile.notes}</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Onboarding Tracking */}
        <View style={styles.card} {...(Platform.OS === 'web' && { className: 'responsive-card' })}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <CalendarCheck size={20} color="#6b7280" />
              <Text style={styles.cardTitle}>Onboarding</Text>
            </View>
          </View>
          {onboardingError && (
            <View style={styles.alertError}>
              <Text style={styles.alertErrorText}>{onboardingError}</Text>
            </View>
          )}
          <View style={styles.onboardingContent}>
            {/* Welcome Card */}
            <View style={styles.onboardingItem}>
              <View style={styles.onboardingItemLeft}>
                <View style={[
                  styles.onboardingIconContainer,
                  profile?.sent_welcome_card_at ? styles.onboardingIconCompleted : styles.onboardingIconPending
                ]}>
                  <Mail size={20} color={profile?.sent_welcome_card_at ? '#ffffff' : '#6b7280'} />
                </View>
                <View style={styles.onboardingItemContent}>
                  <Text style={styles.onboardingItemTitle}>Welcome Card</Text>
                  {profile?.sent_welcome_card_at ? (
                    <Text style={styles.onboardingItemDate}>
                      Sent {new Date(profile.sent_welcome_card_at).toLocaleDateString()}
                    </Text>
                  ) : (
                    <Text style={styles.onboardingItemStatus}>Not sent</Text>
                  )}
                </View>
              </View>
              <Pressable
                onPress={handleUpdateWelcomeCard}
                disabled={updatingOnboarding}
                style={[
                  styles.onboardingButton,
                  profile?.sent_welcome_card_at ? styles.onboardingButtonCompleted : styles.onboardingButtonPending
                ]}
              >
                {updatingOnboarding ? (
                  <ActivityIndicator size="small" color={profile?.sent_welcome_card_at ? '#ffffff' : '#4b5563'} />
                ) : (
                  <>
                    {profile?.sent_welcome_card_at ? (
                      <>
                        <X size={16} color="#ffffff" />
                        <Text style={styles.onboardingButtonTextCompleted}>Mark Unsent</Text>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} color="#4b5563" />
                        <Text style={styles.onboardingButtonTextPending}>Mark as Sent</Text>
                      </>
                    )}
                  </>
                )}
              </Pressable>
            </View>

            {/* Walkthrough */}
            <View style={styles.onboardingItem}>
              <View style={styles.onboardingItemLeft}>
                <View style={[
                  styles.onboardingIconContainer,
                  profile?.had_walkthrough ? styles.onboardingIconCompleted : styles.onboardingIconPending
                ]}>
                  <CalendarCheck size={20} color={profile?.had_walkthrough ? '#ffffff' : '#6b7280'} />
                </View>
                <View style={styles.onboardingItemContent}>
                  <Text style={styles.onboardingItemTitle}>Walkthrough</Text>
                  {profile?.had_walkthrough ? (
                    <Text style={styles.onboardingItemStatus}>Completed</Text>
                  ) : (
                    <Text style={styles.onboardingItemStatus}>Not completed</Text>
                  )}
                </View>
              </View>
              <Pressable
                onPress={handleUpdateWalkthrough}
                disabled={updatingOnboarding}
                style={[
                  styles.onboardingButton,
                  profile?.had_walkthrough ? styles.onboardingButtonCompleted : styles.onboardingButtonPending
                ]}
              >
                {updatingOnboarding ? (
                  <ActivityIndicator size="small" color={profile?.had_walkthrough ? '#ffffff' : '#4b5563'} />
                ) : (
                  <>
                    {profile?.had_walkthrough ? (
                      <>
                        <X size={16} color="#ffffff" />
                        <Text style={styles.onboardingButtonTextCompleted}>Mark Incomplete</Text>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} color="#4b5563" />
                        <Text style={styles.onboardingButtonTextPending}>Mark Complete</Text>
                      </>
                    )}
                  </>
                )}
              </Pressable>
            </View>
          </View>
        </View>

        <View style={[styles.card, styles.cardFullWidth]} {...(Platform.OS === 'web' && { className: 'responsive-card card-full-width' })}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <AlertTriangle size={20} color="#6b7280" />
              <Text style={styles.cardTitle}>Request Capacity</Text>
            </View>
          </View>

          {requestCapacityError && (
            <View style={styles.alertError}>
              <Text style={styles.alertErrorText}>{requestCapacityError}</Text>
            </View>
          )}

          {loadingRequestCapacity ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="small" color="#9ca3af" />
            </View>
          ) : (
            <View style={styles.capacitySection}>
              <View style={styles.capacityMetrics}>
                <View style={styles.capacityMetricCard}>
                  <Text style={styles.capacityMetricLabel}>Active Requests</Text>
                  <Text style={styles.capacityMetricValue}>{requestCapacity?.active_count ?? 0}</Text>
                </View>
                <View style={styles.capacityMetricCard}>
                  <Text style={styles.capacityMetricLabel}>Backburner Requests</Text>
                  <Text style={styles.capacityMetricValue}>{requestCapacity?.backburner_count ?? 0}</Text>
                </View>
                <View style={styles.capacityMetricCard}>
                  <Text style={styles.capacityMetricLabel}>Effective Limit</Text>
                  <Text style={styles.capacityMetricValue}>{requestCapacity?.effective_limit ?? 'N/A'}</Text>
                </View>
                <View style={styles.capacityMetricCard}>
                  <Text style={styles.capacityMetricLabel}>Over Limit</Text>
                  <Text style={[
                    styles.capacityMetricValue,
                    (requestCapacity?.over_limit_count ?? 0) > 0 && styles.capacityMetricValueAlert,
                  ]}>
                    {requestCapacity?.over_limit_count ?? 0}
                  </Text>
                </View>
              </View>

              <View style={styles.capacityForm}>
                <View style={styles.capacityFormField}>
                  <Text style={styles.fieldLabel}>Override Active Limit</Text>
                  <TextInput
                    value={newActiveLimit}
                    onChangeText={setNewActiveLimit}
                    keyboardType="numeric"
                    placeholder="e.g. 3"
                    style={styles.input}
                  />
                </View>
                <View style={styles.capacityFormField}>
                  <Text style={styles.fieldLabel}>Reason (Optional)</Text>
                  <TextInput
                    value={capacityReason}
                    onChangeText={setCapacityReason}
                    placeholder="Temporary high-touch homeowner"
                    style={[styles.input, styles.textArea]}
                    multiline
                    numberOfLines={2}
                  />
                </View>
                <View style={styles.capacityFormActions}>
                  <Pressable
                    onPress={handleSaveRequestLimit}
                    disabled={savingRequestLimit}
                    style={[styles.saveButton, savingRequestLimit && styles.saveButtonDisabled]}
                  >
                    {savingRequestLimit ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.saveButtonText}>Save Override</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Service Request History */}
        <View style={[styles.card, styles.cardFullWidth]} {...(Platform.OS === 'web' && { className: 'responsive-card card-full-width' })}>
          <View style={styles.cardHeaderNoMargin}>
            <MessageSquare size={20} color="#6b7280" />
            <Text style={styles.cardTitle}>Service Request History</Text>
          </View>
          
          {loadingRequests ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : serviceRequests.length === 0 ? (
            <View style={styles.centerContainer}>
              <Text style={styles.emptyText}>No service requests found</Text>
            </View>
          ) : (
            <View style={styles.serviceRequestsList}>
              {serviceRequests.map((request) => {
                const priorityStyle = getPriorityColor(request.priority);
                const statusStyle = getStatusColor(request.status);
                const isExpanded = expandedServiceRequestIds.has(request.id);

                return (
                  <View key={request.id} style={styles.srCompactCard}>
                    <Pressable
                      style={styles.srCompactCardHeader}
                      onPress={() => toggleServiceRequestExpanded(request.id)}
                    >
                      <View style={styles.srCompactCardLeft}>
                        {isExpanded ? (
                          <ChevronDown size={14} color="#9ca3af" />
                        ) : (
                          <ChevronRight size={14} color="#9ca3af" />
                        )}
                        <View style={[styles.srCompactStatusBadge, { backgroundColor: statusStyle.backgroundColor }]}>
                          <Text style={[styles.srCompactStatusText, { color: statusStyle.color }]}>
                            {request.status.split('_').map(word =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Text>
                        </View>
                        <View style={[styles.srCompactPriorityDot, { backgroundColor: priorityStyle.color }]} />
                        <Text style={styles.srCompactTitle} numberOfLines={1}>{request.title}</Text>
                      </View>
                      <View style={styles.srCompactCardRight}>
                        <Text style={styles.srCompactDate}>
                          {new Date(request.created_at).toLocaleDateString()}
                        </Text>
                        <Pressable
                          style={styles.srCompactDetailsButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/service-request/${request.id}`);
                          }}
                        >
                          <ExternalLink size={11} color="#ffffff" />
                        </Pressable>
                      </View>
                    </Pressable>

                    {isExpanded && (
                      <View style={styles.srCompactCardBody}>
                        <Text style={styles.srCompactDescription}>{request.description}</Text>
                        <View style={styles.srCompactMeta}>
                          <Home size={13} color="#9ca3af" />
                          <Text style={styles.srCompactMetaText}>
                            {request.home?.address}
                          </Text>
                        </View>
                        <View style={styles.srCompactBadges}>
                          <View style={[styles.priorityBadge, { backgroundColor: priorityStyle.backgroundColor }]}>
                            <Text style={[styles.priorityBadgeText, { color: priorityStyle.color }]}>
                              {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                            </Text>
                          </View>
                          {(request.is_backburner || request.priority === 'low') && (
                            <View style={styles.backburnerBadge}>
                              <Text style={styles.backburnerBadgeText}>
                                {request.priority === 'low' ? 'Backburner (Low Priority)' : 'Backburner'}
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>

        {/* Properties */}
        <View style={styles.card} {...(Platform.OS === 'web' && { className: 'responsive-card' })}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <Home size={20} color="#6b7280" />
              <Text style={styles.cardTitle}>Properties</Text>
            </View>
            <Pressable
              style={styles.saveButton}
              onPress={() => {
                setHomeError(null);
                setShowAddHomeModal(true);
              }}
            >
              <Text style={styles.saveButtonText}>Add Home</Text>
            </Pressable>
          </View>
          {homeError && (
            <View style={styles.alertError}>
              <Text style={styles.alertErrorText}>{homeError}</Text>
            </View>
          )}
          {homes.length === 0 ? (
            <Text style={styles.emptyText}>No properties registered</Text>
          ) : (
            <View style={styles.homesList}>
              {homes.map(home => (
                <View key={home.id} style={styles.homeCard}>
                  {editingHomeId === home.id ? (
                    <View style={styles.homeEditForm}>
                      <View style={styles.field}>
                        <Text style={styles.fieldLabel}>Address</Text>
                        <TextInput
                          value={editedHome.address || ''}
                          onChangeText={(value) => handleHomeChange('address', value)}
                          placeholder="Enter address"
                          style={styles.input}
                        />
                      </View>
                      <View style={styles.formRow}>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>City</Text>
                          <TextInput
                            value={editedHome.city || ''}
                            onChangeText={(value) => handleHomeChange('city', value)}
                            placeholder="City"
                            style={styles.input}
                          />
                        </View>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>State</Text>
                          <TextInput
                            value={editedHome.state || ''}
                            onChangeText={(value) => handleHomeChange('state', value)}
                            placeholder="State"
                            style={styles.input}
                          />
                        </View>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>ZIP Code</Text>
                          <TextInput
                            value={editedHome.zip_code || ''}
                            onChangeText={(value) => handleHomeChange('zip_code', value)}
                            placeholder="ZIP Code"
                            style={styles.input}
                          />
                        </View>
                      </View>
                      <View style={styles.formRow}>
                        <View style={styles.formCol2}>
                          <Text style={styles.fieldLabel}>Square Footage</Text>
                          <TextInput
                            value={editedHome.square_footage?.toString() || ''}
                            onChangeText={(value) => handleHomeChange('square_footage', value ? parseInt(value) : null)}
                            placeholder="Square footage"
                            keyboardType="numeric"
                            style={styles.input}
                          />
                        </View>
                        <View style={styles.formCol2}>
                          <Text style={styles.fieldLabel}>Year Built</Text>
                          <TextInput
                            value={editedHome.year_built?.toString() || ''}
                            onChangeText={(value) => handleHomeChange('year_built', value ? parseInt(value) : null)}
                            placeholder="Year built"
                            keyboardType="numeric"
                            style={styles.input}
                          />
                        </View>
                      </View>
                      <View style={styles.formRow}>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>Lot Size (sq ft)</Text>
                          <TextInput
                            value={editedHome.lot_size?.toString() || ''}
                            onChangeText={(value) => handleHomeChange('lot_size', value ? parseInt(value) : null)}
                            placeholder="Lot size"
                            keyboardType="numeric"
                            style={styles.input}
                          />
                        </View>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>Bedrooms</Text>
                          <TextInput
                            value={editedHome.bedrooms?.toString() || ''}
                            onChangeText={(value) => handleHomeChange('bedrooms', value ? parseInt(value) : null)}
                            placeholder="Bedrooms"
                            keyboardType="numeric"
                            style={styles.input}
                          />
                        </View>
                        <View style={styles.formCol3}>
                          <Text style={styles.fieldLabel}>Bathrooms</Text>
                          <TextInput
                            value={editedHome.bathrooms?.toString() || ''}
                            onChangeText={(value) => handleHomeChange('bathrooms', value ? parseFloat(value) : null)}
                            placeholder="Bathrooms"
                            keyboardType="decimal-pad"
                            style={styles.input}
                          />
                        </View>
                      </View>
                      <View style={styles.field}>
                        <Text style={styles.fieldLabel}>Cover Photo</Text>
                          {editedHome.cover_photo_url ? (
                          <View style={styles.imagePreviewContainer}>
                            {Platform.OS === 'web' && (
                              <img
                                src={editedHome.cover_photo_url}
                                alt="Cover photo preview"
                                style={{
                                  width: '100%',
                                  height: 128,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  border: '1px solid #e5e7eb'
                                }}
                              />
                            )}
                            <Pressable
                              onPress={() => handleHomeChange('cover_photo_url', '')}
                              style={styles.removeImageButton}
                            >
                              <X size={16} color="#ffffff" />
                            </Pressable>
                          </View>
                        ) : (
                          <View style={styles.uploadBox}>
                            <ImageIcon size={24} color="#9ca3af" />
                            <Text style={styles.uploadText}>Upload a cover photo</Text>
                            {Platform.OS === 'web' && (
                              <label style={{ cursor: 'pointer' }}>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                  style={{ display: 'none' }}
                                  disabled={uploading}
                                />
                                <View style={styles.uploadButton}>
                                  {uploading ? (
                                    <>
                                      <ActivityIndicator size="small" color="#374151" />
                                      <Text style={styles.uploadButtonText}>Uploading...</Text>
                                    </>
                                  ) : (
                                    <>
                                      <Upload size={16} color="#374151" />
                                      <Text style={styles.uploadButtonText}>Choose File</Text>
                                    </>
                                  )}
                                </View>
                              </label>
                          )}
                          {uploadError && (
                              <Text style={styles.errorText}>{uploadError}</Text>
                            )}
                          </View>
                        )}
                      </View>
                      <View style={styles.field}>
                        <Text style={styles.fieldLabel}>Home Notes</Text>
                        <TextInput
                          value={editedHome.home_notes || ''}
                          onChangeText={(value) => handleHomeChange('home_notes', value)}
                          placeholder="Add notes about this property..."
                          multiline
                          numberOfLines={3}
                          style={[styles.input, styles.textArea]}
                        />
                      </View>
                      <View style={styles.homeEditActions}>
                        <Pressable
                          onPress={handleCancelHomeEdit}
                          disabled={savingHome}
                          style={styles.cancelButton}
                        >
                          <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                          onPress={handleSaveHome}
                          disabled={savingHome}
                          style={[styles.saveButton, savingHome && styles.saveButtonDisabled]}
                        >
                          <Text style={styles.saveButtonText}>
                          {savingHome ? 'Saving...' : 'Save'}
                          </Text>
                        </Pressable>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.homeCardContent}>
                      <Pressable
                        onPress={() => navigate(`/admin/home?id=${home.id}&userId=${userId}`)}
                        style={styles.homeCardPressable}
                      >
                        <View style={styles.homeCardRow}>
                          {home.cover_photo_url && Platform.OS === 'web' && (
                                <img
                                  src={home.cover_photo_url}
                                  alt="Home cover"
                              style={{
                                width: 64,
                                height: 64,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #e5e7eb',
                                flexShrink: 0
                              }}
                            />
                          )}
                          <View style={styles.homeCardDetails}>
                            <View style={styles.homeCardTitle}>
                              <Text style={styles.homeCardAddress}>{home.address}</Text>
                              <ExternalLink size={16} color="#2563eb" style={{ opacity: 0 }} />
                            </View>
                            <Text style={styles.homeCardLocation}>
                                  {home.city}, {home.state} {home.zip_code}
                            </Text>
                            <Text style={styles.homeCardMeta}>
                                  {home.square_footage && `${home.square_footage} sq ft • `}
                                  {home.year_built && `Built ${home.year_built}`}
                                  {home.bedrooms && ` • ${home.bedrooms} bed${home.bedrooms !== 1 ? 's' : ''}`}
                                  {home.bathrooms && ` • ${home.bathrooms} bath${home.bathrooms !== 1 ? 's' : ''}`}
                                  {home.lot_size && ` • ${home.lot_size.toLocaleString()} sq ft lot`}
                            </Text>
                                {home.home_notes && (
                              <View style={styles.homeNotesBox}>
                                <Text style={styles.homeNotesText}>
                                  <Text style={styles.homeNotesLabel}>Notes:</Text> {home.home_notes}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </Pressable>
                      <Pressable
                        onPress={() => handleEditHome(home)}
                        style={styles.homeEditButton}
                      >
                        <Text style={styles.homeEditButtonText}>Edit</Text>
                      </Pressable>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
          </View>
        </LayoutContainer>

        {/* Timeline */}
        <LayoutContainer className={Platform.OS === 'web' ? 'timeline-sidebar' : undefined} style={Platform.OS === 'web' ? undefined : styles.timelineSidebar}>
          <View style={styles.card} {...(Platform.OS === 'web' && { className: 'responsive-card' })}>
          <View style={styles.cardHeaderNoMargin}>
            <Clock size={20} color="#6b7280" />
            <Text style={styles.cardTitle}>Timeline</Text>
          </View>
          {loadingVisits ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="small" color="#9ca3af" />
            </View>
          ) : (
            <View style={styles.timeline}>
              {(() => {
                // Build timeline events array
                const timelineEvents: Array<{
                  date: Date;
                  type: string;
                  content: React.ReactNode;
                  icon?: React.ReactNode;
                }> = [];

                if (!profile) return null;

                // Account created
                timelineEvents.push({
                  date: new Date(profile.created_at),
                  type: 'account_created',
                  content: <Text style={styles.timelineText}>Account created</Text>,
                  icon: <User size={16} color="#6b7280" />
                });

                // Welcome card sent
                if (profile.sent_welcome_card_at) {
                  timelineEvents.push({
                    date: new Date(profile.sent_welcome_card_at),
                    type: 'welcome_card',
                    content: (
                      <Text style={styles.timelineText}>
                        Welcome card sent
                      </Text>
                    ),
                    icon: <Mail size={16} color="#10b981" />
                  });
                }

                // Walkthrough occurrence (when lifecycle status changed to walkthrough)
                if (profile.lifecycle_status === 'walkthrough' && profile.lifecycle_updated_at) {
                  timelineEvents.push({
                    date: new Date(profile.lifecycle_updated_at),
                    type: 'walkthrough',
                    content: (
                      <Text style={styles.timelineText}>
                        Walkthrough started
                      </Text>
                    ),
                    icon: <CalendarCheck size={16} color="#2563eb" />
                  });
                }

                // Lifecycle status updates (if different from walkthrough)
                if (profile.lifecycle_updated_at && profile.lifecycle_status !== 'walkthrough') {
                  timelineEvents.push({
                    date: new Date(profile.lifecycle_updated_at),
                    type: 'lifecycle_update',
                    content: (
                      <Text style={styles.timelineText}>
                        Lifecycle status updated to{' '}
                        <Text style={[styles.timelineTextBold, styles.capitalize]}>
                          {profile.lifecycle_status}
                        </Text>
                      </Text>
                    ),
                    icon: <Clock size={16} color="#f59e0b" />
                  });
                }

                // Service requests
                serviceRequests.forEach((request) => {
                  timelineEvents.push({
                    date: new Date(request.created_at),
                    type: 'service_request',
                    content: (
                      <Text style={styles.timelineText}>
                        Service request created:{' '}
                        <Text style={styles.timelineTextBold}>{request.title}</Text>
                        {request.home && (
                          <Text style={styles.timelineTextSecondary}>
                            {' '}at {request.home.address}
                          </Text>
                        )}
                      </Text>
                    ),
                    icon: <MessageSquare size={16} color="#ea580c" />
                  });

                  // Visit scheduled for service request
                  if (request.visit_scheduled_at) {
                    timelineEvents.push({
                      date: new Date(request.visit_scheduled_at),
                      type: 'visit_scheduled',
                      content: (
                        <Text style={styles.timelineText}>
                          Visit scheduled for:{' '}
                          <Text style={styles.timelineTextBold}>{request.title}</Text>
                        </Text>
                      ),
                      icon: <Calendar size={16} color="#2563eb" />
                    });
                  }

                  // Visit completed for service request
                  if (request.visit_completed_at) {
                    timelineEvents.push({
                      date: new Date(request.visit_completed_at),
                      type: 'visit_completed',
                      content: (
                        <Text style={styles.timelineText}>
                          Visit completed for:{' '}
                          <Text style={styles.timelineTextBold}>{request.title}</Text>
                        </Text>
                      ),
                      icon: <CheckCircle2 size={16} color="#16a34a" />
                    });
                  }
                });

                // Visits
                (visits || []).forEach((visit) => {
                  if (!visit.scheduled_date) return;
                  
                  const visitDate = visit.actual_end_time 
                    ? new Date(visit.actual_end_time)
                    : visit.actual_start_time
                    ? new Date(visit.actual_start_time)
                    : new Date(visit.scheduled_date);

                  const visitTypeLabel = visit.visit_type === 'maintenance' ? 'Maintenance' : visit.visit_type === 'walkthrough' ? 'Walkthrough' : 'Service';
                  const statusLabel = visit.status ? visit.status.split('_').map((word: string) => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ') : '';

                  timelineEvents.push({
                    date: visitDate,
                    type: 'visit',
                    content: (
                      <Text style={styles.timelineText}>
                        {visitTypeLabel} visit {visit.status === 'completed' ? 'completed' : visit.status === 'scheduled' ? 'scheduled' : statusLabel.toLowerCase()}
                        {visit.home && (
                          <Text style={styles.timelineTextSecondary}>
                            {' '}at {visit.home.address}
                          </Text>
                        )}
                        {visit.assigned_to && (
                          <Text style={styles.timelineTextSecondary}>
                            {' '}({visit.assigned_to})
                          </Text>
                        )}
                      </Text>
                    ),
                    icon: visit.status === 'completed' 
                      ? <CheckCircle2 size={16} color="#16a34a" />
                      : <Calendar size={16} color="#2563eb" />
                  });
                });

                // Sort events by date (most recent first)
                timelineEvents.sort((a, b) => b.date.getTime() - a.date.getTime());

                if (timelineEvents.length === 0) {
                  return (
                    <View style={styles.centerContainer}>
                      <Text style={styles.emptyText}>No timeline events yet</Text>
                    </View>
                  );
                }

                return (
                  <>
                    <View style={styles.timelineItem}>
                      <View style={styles.timelineDate}>
                        <Text style={styles.timelineDateText}>Now</Text>
                      </View>
                      <View style={styles.timelineLine} />
                    </View>
                    {timelineEvents.map((event, index) => (
                      <View key={`${event.type}-${event.date.getTime()}-${index}`} style={styles.timelineItem}>
                        <View style={styles.timelineDate}>
                          <Text style={styles.timelineDateText}>
                            {event.date.toLocaleDateString()}
                          </Text>
                        </View>
                        <View style={styles.timelineContent}>
                          <View style={styles.timelineContentRow}>
                            {event.icon && (
                              <View style={styles.timelineIcon}>
                                {event.icon}
                              </View>
                            )}
                            {event.content}
                          </View>
                        </View>
                      </View>
                    ))}
                  </>
                );
              })()}
            </View>
          )}
          </View>
        </LayoutContainer>
      </LayoutContainer>

      {/* Service Request Detail Modal */}
      {showServiceRequestModal && selectedServiceRequest && (
        <Modal
          visible={showServiceRequestModal}
          transparent
          animationType="slide"
          onRequestClose={() => setShowServiceRequestModal(false)}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={() => setShowServiceRequestModal(false)} />
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <View style={styles.modalHeaderContent}>
                  <Text style={styles.modalTitle}>{selectedServiceRequest.title}</Text>
                  <View style={styles.modalHeaderMeta}>
                    <View style={styles.modalHeaderMetaItem}>
                      <Home size={16} color="#6b7280" />
                      <Text style={styles.modalHeaderMetaText}>
                      {selectedServiceRequest.home?.address}, {selectedServiceRequest.home?.city}, {selectedServiceRequest.home?.state}
                      </Text>
                    </View>
                    <View style={styles.modalHeaderMetaItem}>
                      <Clock size={16} color="#6b7280" />
                      <Text style={styles.modalHeaderMetaText}>
                      Created: {new Date(selectedServiceRequest.created_at).toLocaleDateString()}
                      </Text>
                    </View>
                  </View>
                </View>
                <Pressable
                  onPress={() => setShowServiceRequestModal(false)}
                  style={styles.modalCloseButton}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Description</Text>
                  <View style={styles.modalDescriptionBox}>
                    <Text style={styles.modalDescriptionText}>{selectedServiceRequest.description}</Text>
                  </View>
                </View>

                <View style={styles.modalGrid}>
                  <View style={styles.modalGridItem}>
                    <Text style={styles.modalSectionTitle}>Priority</Text>
                    <View style={[
                      styles.modalBadge,
                      { backgroundColor: getPriorityColor(selectedServiceRequest.priority).backgroundColor }
                    ]}>
                      <Text style={[
                        styles.modalBadgeText,
                        { color: getPriorityColor(selectedServiceRequest.priority).color }
                      ]}>
                    {selectedServiceRequest.priority.charAt(0).toUpperCase() + selectedServiceRequest.priority.slice(1)}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.modalGridItem}>
                    <Text style={styles.modalSectionTitle}>Status</Text>
                    <View style={[
                      styles.modalBadge,
                      { backgroundColor: getStatusColor(selectedServiceRequest.status).backgroundColor }
                    ]}>
                      <Text style={[
                        styles.modalBadgeText,
                        { color: getStatusColor(selectedServiceRequest.status).color }
                      ]}>
                    {selectedServiceRequest.status.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                      </Text>
                    </View>
                  </View>
                </View>

              {selectedServiceRequest.visit_scheduled_at && (
                  <View style={styles.modalSection}>
                    <Text style={styles.modalSectionTitle}>Visit Information</Text>
                    <View style={styles.visitInfoBox}>
                      <View style={styles.visitInfoItem}>
                        <Calendar size={16} color="#1d4ed8" />
                        <Text style={styles.visitInfoText}>
                        Visit Scheduled: {new Date(selectedServiceRequest.visit_scheduled_at).toLocaleDateString()}
                        </Text>
                      </View>
                    {selectedServiceRequest.visit_completed_at && (
                        <View style={styles.visitInfoItem}>
                          <Clock size={16} color="#15803d" />
                          <Text style={styles.visitCompletedText}>
                          Completed: {new Date(selectedServiceRequest.visit_completed_at).toLocaleDateString()}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable
                  onPress={() => navigate(`/admin/service-request/${selectedServiceRequest.id}`)}
                  style={styles.viewDetailsButton}
                >
                  <Text style={styles.viewDetailsButtonText}>View Full Details</Text>
                </Pressable>
                <Pressable
                  onPress={() => setShowServiceRequestModal(false)}
                  style={styles.closeButton}
                >
                  <Text style={styles.closeButtonText}>Close</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {showAddHomeModal && (
        <Modal
          visible={showAddHomeModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowAddHomeModal(false)}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={() => setShowAddHomeModal(false)} />
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Add Home</Text>
                <Pressable onPress={() => setShowAddHomeModal(false)} style={styles.modalCloseButton}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>
              <View style={styles.modalBody}>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>Address</Text>
                  <TextInput
                    value={newHomeData.address}
                    onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, address: value }))}
                    style={styles.input}
                    placeholder="Street address"
                  />
                </View>
                <View style={styles.formRow}>
                  <View style={styles.formCol3}>
                    <Text style={styles.fieldLabel}>City</Text>
                    <TextInput
                      value={newHomeData.city}
                      onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, city: value }))}
                      style={styles.input}
                    />
                  </View>
                  <View style={styles.formCol3}>
                    <Text style={styles.fieldLabel}>State</Text>
                    <TextInput
                      value={newHomeData.state}
                      onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, state: value }))}
                      style={styles.input}
                    />
                  </View>
                  <View style={styles.formCol3}>
                    <Text style={styles.fieldLabel}>ZIP</Text>
                    <TextInput
                      value={newHomeData.zip_code}
                      onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, zip_code: value }))}
                      style={styles.input}
                    />
                  </View>
                </View>
                <View style={styles.formRow}>
                  <View style={styles.formCol2}>
                    <Text style={styles.fieldLabel}>Square Footage</Text>
                    <TextInput
                      value={newHomeData.square_footage}
                      onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, square_footage: value }))}
                      style={styles.input}
                      keyboardType="numeric"
                    />
                  </View>
                  <View style={styles.formCol2}>
                    <Text style={styles.fieldLabel}>Year Built</Text>
                    <TextInput
                      value={newHomeData.year_built}
                      onChangeText={(value) => setNewHomeData((prev) => ({ ...prev, year_built: value }))}
                      style={styles.input}
                      keyboardType="numeric"
                    />
                  </View>
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>Home Manager</Text>
                  <Pressable style={styles.dropdownTrigger} onPress={() => setShowAddHomeManagerPicker(true)}>
                    <Text style={styles.dropdownTriggerText}>{newHomeData.home_manager}</Text>
                    <ChevronDown size={16} color="#6b7280" />
                  </Pressable>
                </View>
              </View>
              <View style={styles.modalFooter}>
                <Pressable onPress={() => setShowAddHomeModal(false)} style={styles.closeButton}>
                  <Text style={styles.closeButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleCreateHome}
                  style={[styles.viewDetailsButton, addingHome && styles.disabledButton]}
                  disabled={addingHome}
                >
                  {addingHome ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.viewDetailsButtonText}>Create Home</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      </LayoutContainer>

      <DropdownPicker
        visible={showLifecyclePicker}
        onClose={() => setShowLifecyclePicker(false)}
        options={[
          { value: 'lead', label: 'Lead' },
          { value: 'walkthrough', label: 'Walkthrough' },
          { value: 'first_visit', label: 'First Visit' },
          { value: 'active', label: 'Active' },
          { value: 'churned', label: 'Churned' }
        ]}
        selectedValue={editedProfile.lifecycle_status || ''}
        onSelect={(value) => handleChange('lifecycle_status', value)}
      />

      <DropdownPicker
        visible={showAddHomeManagerPicker}
        onClose={() => setShowAddHomeManagerPicker(false)}
        options={[
          { value: 'Bradley Curtis', label: 'Bradley Curtis' },
          { value: 'Brandon Takens', label: 'Brandon Takens' },
          { value: 'Bryce Kaiser', label: 'Bryce Kaiser' },
          { value: 'Unassigned', label: 'Unassigned' },
        ]}
        selectedValue={newHomeData.home_manager}
        onSelect={(value) => setNewHomeData((prev) => ({ ...prev, home_manager: value }))}
      />

      {/* Generate Link Modal */}
      <Modal
        visible={showLinkModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowLinkModal(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShowLinkModal(false)} />
          <View style={styles.linkModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Account Claim Link</Text>
              <Pressable onPress={() => setShowLinkModal(false)}>
                <X size={20} color="#6b7280" />
              </Pressable>
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.modalDescriptionText}>
                Share this link with the homeowner. It will allow them to claim their account and set a password.
              </Text>
              <View style={styles.linkContainer}>
                <Text style={styles.linkText} numberOfLines={1} ellipsizeMode="middle">
                  {claimLink}
                </Text>
                <Pressable onPress={handleCopyLink} style={styles.copyButton}>
                  <Copy size={20} color="#4b5563" />
                </Pressable>
              </View>
            </View>
            <View style={styles.modalFooter}>
              <Pressable
                onPress={() => setShowLinkModal(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>Done</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal
        visible={showDeleteConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => !deleting && setShowDeleteConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable 
            style={styles.modalBackdrop} 
            onPress={() => !deleting && setShowDeleteConfirmModal(false)} 
          />
          <View style={styles.deleteModalContent}>
            <View style={styles.deleteModalHeader}>
              <View style={styles.deleteModalIconContainer}>
                <AlertTriangle size={24} color="#dc2626" />
              </View>
              <Text style={styles.deleteModalTitle}>Delete User</Text>
            </View>
            
            <View style={styles.deleteModalBody}>
              <Text style={styles.deleteModalText}>
                Are you sure you want to delete this user? This action cannot be undone.
              </Text>
              {profile && (
                <View style={styles.deleteModalUserInfo}>
                  <Text style={styles.deleteModalUserLabel}>User:</Text>
                  <Text style={styles.deleteModalUserName}>{profile.full_name || 'Unknown'}</Text>
                </View>
              )}
              <View style={styles.deleteModalWarningBox}>
                <Text style={styles.deleteModalWarningText}>
                  <Text style={styles.deleteModalWarningBold}>Warning:</Text> This will permanently delete:
                </Text>
                <View style={styles.deleteModalWarningList}>
                  <Text style={styles.deleteModalWarningItem}>• User profile</Text>
                  <Text style={styles.deleteModalWarningItem}>• All associated homes ({homes.length})</Text>
                  <Text style={styles.deleteModalWarningItem}>• All service requests ({serviceRequests.length})</Text>
                  <Text style={styles.deleteModalWarningItem}>• All related data (tasks, visits, expenses, etc.)</Text>
                </View>
              </View>
              {deleteError && (
                <View style={styles.alertError}>
                  <Text style={styles.alertErrorText}>{deleteError}</Text>
                </View>
              )}
            </View>

            <View style={styles.deleteModalFooter}>
              <Pressable
                onPress={() => {
                  setShowDeleteConfirmModal(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
                style={[styles.deleteModalCancelButton, deleting && styles.deleteModalButtonDisabled]}
              >
                <Text style={styles.deleteModalCancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={handleDeleteUser}
                disabled={deleting}
                style={[styles.deleteModalConfirmButton, deleting && styles.deleteModalButtonDisabled]}
              >
                {deleting ? (
                  <>
                    <ActivityIndicator size="small" color="#ffffff" />
                    <Text style={styles.deleteModalConfirmButtonText}>Deleting...</Text>
                  </>
                ) : (
                  <Text style={styles.deleteModalConfirmButtonText}>Delete User</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Stripe Customer Modal */}
      <Modal
        visible={showStripeCustomerModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowStripeCustomerModal(false);
          setExistingCustomerId('');
          setStripeCustomerError(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <Pressable 
            style={styles.modalBackdrop} 
            onPress={() => {
              setShowStripeCustomerModal(false);
              setExistingCustomerId('');
              setStripeCustomerError(null);
            }}
          />
          <View style={styles.linkModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Connect Stripe Customer</Text>
              <Pressable onPress={() => {
                setShowStripeCustomerModal(false);
                setExistingCustomerId('');
                setStripeCustomerError(null);
              }}>
                <X size={20} color="#6b7280" />
              </Pressable>
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.modalDescriptionText}>
                Create a new Stripe customer or link an existing one for {profile?.full_name}.
              </Text>

              {stripeCustomerError && (
                <View style={styles.alertError}>
                  <Text style={styles.alertErrorText}>{stripeCustomerError}</Text>
                </View>
              )}

              <View style={{ gap: 12, marginTop: 16 }}>
                <Pressable
                  onPress={() => handleCreateStripeCustomer(false)}
                  disabled={creatingStripeCustomer}
                  style={[styles.stripeModalButton, styles.stripeModalButtonPrimary]}
                >
                  {creatingStripeCustomer && !existingCustomerId ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.stripeModalButtonText}>Create New Customer</Text>
                  )}
                </Pressable>

                <View style={styles.dividerContainer}>
                  <View style={styles.divider} />
                  <Text style={styles.dividerText}>OR</Text>
                  <View style={styles.divider} />
                </View>

                <View style={{ gap: 8 }}>
                  <Text style={styles.fieldLabel}>Link Existing Customer ID</Text>
                  <TextInput
                    style={styles.input}
                    value={existingCustomerId}
                    onChangeText={setExistingCustomerId}
                    placeholder="cus_xxxxxxxxxxxx"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <Text style={{ fontSize: 12, color: '#6b7280' }}>
                    Enter the Stripe customer ID (starts with "cus_")
                  </Text>
                </View>

                <Pressable
                  onPress={() => handleCreateStripeCustomer(true)}
                  disabled={creatingStripeCustomer || !existingCustomerId.trim()}
                  style={[
                    styles.stripeModalButton,
                    styles.stripeModalButtonSecondary,
                    (!existingCustomerId.trim() || creatingStripeCustomer) && styles.stripeModalButtonDisabled
                  ]}
                >
                  {creatingStripeCustomer && existingCustomerId ? (
                    <ActivityIndicator size="small" color="#5469d4" />
                  ) : (
                    <Text style={[styles.stripeModalButtonText, { color: '#5469d4' }]}>
                      Link Existing Customer
                    </Text>
                  )}
                </Pressable>
              </View>
            </View>
            <View style={styles.modalFooter}>
              <Pressable
                onPress={() => {
                  setShowStripeCustomerModal(false);
                  setExistingCustomerId('');
                  setStripeCustomerError(null);
                }}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </WebScrollView>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  contentContainer: {
    padding: 24,
    maxWidth: 1024,
    alignSelf: 'center',
    // On native, apply width. On web, CSS classes handle layout
    ...(Platform.OS !== 'web' ? {
      width: '100%',
    } : {}),
    // On web, CSS classes handle max-width via media queries
    ...(Platform.OS === 'web' ? {} : {}),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  errorContainer: {
    padding: 16,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  headerSection: {
    marginBottom: 24,
    gap: 12,
    width: '100%',
    maxWidth: '100%',
  },
  headerActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  backButtonText: {
    color: '#6b7280',
    fontSize: 14,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionButtonText: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  linkModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 500,
    width: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    padding: 8,
    marginTop: 16,
    gap: 8,
  },
  linkText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  copyButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fecaca',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#dc2626',
    fontWeight: '500',
  },
  mainLayout: {
    gap: 24,
    // On native, apply flex styles. On web, CSS classes handle layout
    ...(Platform.OS !== 'web' ? {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    } : {}),
  },
  mainContent: {
    gap: 24,
    // On native, apply flex styles. On web, CSS classes handle layout
    ...(Platform.OS !== 'web' ? {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    } : {}),
  },
  timelineSidebar: {
    gap: 24,
    // On native, apply flex styles. On web, CSS classes handle layout
    ...(Platform.OS !== 'web' ? {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    } : {}),
  },
  grid: {
    gap: 24,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 24,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
  },
  cardFullWidth: {
    ...(Platform.OS === 'web' && {
      gridColumn: '1 / -1',
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    gap: 16,
    flexWrap: 'wrap',
  },
  cardHeaderNoMargin: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 8,
  },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  editButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  cancelButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  saveButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#000000',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    color: '#ffffff',
  },
  disabledButton: {
    opacity: 0.6,
  },
  dropdownTrigger: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownTriggerText: {
    fontSize: 14,
    color: '#111827',
  },
  alertError: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 6,
  },
  alertErrorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  fieldsList: {
    gap: 16,
  },
  field: {
    gap: 4,
  },
  fieldLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  fieldValue: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  fieldValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  emailEditor: {
    gap: 8,
  },
  inlineActionButton: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  inlineActionButtonText: {
    fontSize: 12,
    color: '#111827',
    fontWeight: '500',
  },
  inlineErrorText: {
    color: '#dc2626',
    fontSize: 12,
  },
  inlineSuccessText: {
    color: '#166534',
    fontSize: 12,
  },
  inlineNoteText: {
    color: '#6b7280',
    fontSize: 12,
  },
  input: {
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  pickerButton: {
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  badgePremium: {
    backgroundColor: '#000000',
  },
  badgeBasic: {
    backgroundColor: '#f3f4f6',
  },
  badgeText: {
    fontSize: 14,
  },
  badgeTextPremium: {
    color: '#ffffff',
  },
  badgeTextBasic: {
    color: '#1f2937',
  },
  badgeStripeActive: {
    backgroundColor: '#dcfce7',
  },
  badgeTextStripeActive: {
    color: '#166534',
  },
  badgeStripeInactive: {
    backgroundColor: '#f3f4f6',
  },
  badgeTextStripeInactive: {
    color: '#6b7280',
  },
  stripeButton: {
    marginLeft: 8,
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#5469d4',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  stripeButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  notesBox: {
    marginTop: 4,
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 6,
  },
  notesText: {
    fontSize: 14,
    color: '#111827',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: 14,
  },
  homesList: {
    gap: 16,
  },
  homeCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 16,
  },
  homeEditForm: {
    gap: 16,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  formCol2: {
    flex: 1,
  },
  formCol3: {
    flex: 1,
  },
  imagePreviewContainer: {
    position: 'relative',
    marginTop: 8,
  },
  removeImageButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#ef4444',
    borderRadius: 12,
    padding: 4,
  },
  uploadBox: {
    marginTop: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    gap: 8,
  },
  uploadText: {
    fontSize: 14,
    color: '#6b7280',
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  uploadButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  homeEditActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
  homeCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  homeCardPressable: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeCardRow: {
    flexDirection: 'row',
    gap: 12,
  },
  homeCardDetails: {
    flex: 1,
    gap: 4,
  },
  homeCardTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  homeCardAddress: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  homeCardLocation: {
    fontSize: 14,
    color: '#6b7280',
  },
  homeCardMeta: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 8,
  },
  homeNotesBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#f9fafb',
    borderRadius: 4,
  },
  homeNotesText: {
    fontSize: 14,
    color: '#4b5563',
  },
  homeNotesLabel: {
    fontWeight: 'bold',
  },
  homeEditButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    alignSelf: 'flex-start',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeEditButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  timeline: {
    gap: 16,
  },
  timelineItem: {
    flexDirection: 'row',
    gap: 16,
  },
  timelineDate: {
    width: 48,
  },
  timelineDateText: {
    fontSize: 14,
    color: '#6b7280',
  },
  timelineLine: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    height: 1,
    marginTop: 8,
  },
  timelineContent: {
    flex: 1,
    marginLeft: 16,
  },
  timelineContentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  timelineIcon: {
    width: 20,
    alignItems: 'center',
  },
  timelineText: {
    fontSize: 14,
    color: '#111827',
    flex: 1,
  },
  timelineTextBold: {
    fontWeight: '500',
  },
  timelineTextSecondary: {
    fontSize: 14,
    color: '#6b7280',
  },
  centerContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  capacitySection: {
    gap: 16,
  },
  capacityMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  capacityMetricCard: {
    flex: 1,
    minWidth: 140,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
    gap: 4,
  },
  capacityMetricLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  capacityMetricValue: {
    fontSize: 20,
    color: '#111827',
    fontWeight: '700',
  },
  capacityMetricValueAlert: {
    color: '#b91c1c',
  },
  capacityForm: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 12,
    gap: 12,
  },
  capacityFormField: {
    gap: 4,
  },
  capacityFormActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  serviceRequestsList: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  srCompactCard: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  srCompactCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  srCompactCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  srCompactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  srCompactStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  srCompactPriorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  srCompactTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
    minWidth: 0,
  },
  srCompactCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  srCompactDate: {
    fontSize: 11,
    color: '#9ca3af',
  },
  srCompactDetailsButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#111827',
    justifyContent: 'center',
    alignItems: 'center',
  },
  srCompactCardBody: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#fafafa',
    gap: 8,
  },
  srCompactDescription: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
  },
  srCompactMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  srCompactMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  srCompactBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  serviceRequestBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  priorityBadgeText: {
    fontSize: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusBadgeText: {
    fontSize: 12,
  },
  backburnerBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#f5f3ff',
  },
  backburnerBadgeText: {
    fontSize: 12,
    color: '#6d28d9',
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 672,
    width: '100%',
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalHeaderContent: {
    flex: 1,
    gap: 8,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalHeaderMeta: {
    gap: 16,
  },
  modalHeaderMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  modalHeaderMetaText: {
    fontSize: 14,
    color: '#6b7280',
  },
  modalCloseButton: {
    padding: 4,
    marginLeft: 16,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  modalBody: {
    flex: 1,
    padding: 24,
  },
  modalSection: {
    marginBottom: 24,
  },
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 8,
  },
  modalDescriptionBox: {
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
  },
  modalDescriptionText: {
    fontSize: 14,
    color: '#374151',
  },
  modalGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 24,
  },
  modalGridItem: {
    flex: 1,
  },
  modalBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  modalBadgeText: {
    fontSize: 14,
  },
  visitInfoBox: {
    padding: 16,
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    gap: 8,
  },
  visitInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  visitInfoText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1d4ed8',
  },
  visitCompletedText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#15803d',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  viewDetailsButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#2563eb',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  viewDetailsButtonText: {
    fontSize: 14,
    color: '#ffffff',
  },
  closeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  closeButtonText: {
    fontSize: 14,
    color: '#ffffff',
  },
  onboardingContent: {
    gap: 16,
    paddingTop: 8,
  },
  onboardingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  onboardingItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  onboardingIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  onboardingIconCompleted: {
    backgroundColor: '#10b981',
  },
  onboardingIconPending: {
    backgroundColor: '#f3f4f6',
  },
  onboardingItemContent: {
    flex: 1,
  },
  onboardingItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  onboardingItemStatus: {
    fontSize: 13,
    color: '#6b7280',
  },
  onboardingItemDate: {
    fontSize: 13,
    color: '#10b981',
    fontWeight: '500',
  },
  onboardingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  onboardingButtonCompleted: {
    backgroundColor: '#ef4444',
  },
  onboardingButtonPending: {
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  onboardingButtonTextCompleted: {
    fontSize: 13,
    fontWeight: '500',
    color: '#ffffff',
  },
  onboardingButtonTextPending: {
    fontSize: 13,
    fontWeight: '500',
    color: '#4b5563',
  },
});

const pickerStyles = StyleSheet.create({
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '60%',
    ...Platform.select({
      web: {
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
      },
    }),
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    flex: 1,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f9fafb',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
  deleteModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    maxWidth: 500,
    width: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  deleteModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  deleteModalIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fef2f2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  deleteModalBody: {
    padding: 24,
    gap: 16,
  },
  deleteModalText: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 24,
  },
  deleteModalUserInfo: {
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    gap: 4,
  },
  deleteModalUserLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  deleteModalUserName: {
    fontSize: 16,
    color: '#111827',
    fontWeight: '600',
  },
  deleteModalWarningBox: {
    padding: 16,
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fecaca',
    gap: 8,
  },
  deleteModalWarningText: {
    fontSize: 14,
    color: '#991b1b',
    lineHeight: 20,
  },
  deleteModalWarningBold: {
    fontWeight: '600',
  },
  deleteModalWarningList: {
    marginLeft: 8,
    gap: 4,
  },
  deleteModalWarningItem: {
    fontSize: 14,
    color: '#991b1b',
    lineHeight: 20,
  },
  deleteModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  deleteModalCancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteModalCancelButtonText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  deleteModalConfirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#dc2626',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  deleteModalConfirmButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  deleteModalButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 8,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: '#e5e7eb',
  },
  dividerText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  stripeModalButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  stripeModalButtonPrimary: {
    backgroundColor: '#5469d4',
  },
  stripeModalButtonSecondary: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#5469d4',
  },
  stripeModalButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  stripeModalButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
  },
  modalBody: {
    padding: 24,
  },
  modalFooter: {
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    alignItems: 'flex-end',
  },
  alertError: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  alertErrorText: {
    color: '#dc2626',
    fontSize: 14,
  },
});

export default ProfileDetail;
